import { getToken } from '../providers/AuthProvider';

/** A custom error class to hold status codes & optional response body. */
export class FetchError<T = undefined> extends Error {
  status: number;
  body?: T;

  constructor(message: string, status: number, body?: T) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** On success, we return { ok: true, data: T }. */
interface SuccessResult<T = undefined> {
  ok: true;
  data: T;
}

/** On failure, we return { ok: false, error: FetchError<E> }. */
interface FailResult<E = undefined> {
  ok: false;
  error: FetchError<E>;
}

/**
 * Combine both success & failure shapes.
 */
type ApiResult<T = undefined, E = undefined> = SuccessResult<T> | FailResult<E>;

/**
 * Handles low-level fetch Response objects by:
 *  - Checking `response.ok`
 *  - Attempting to parse `response.json()`
 *  - Returning either { ok: true, data } or { ok: false, error: FetchError<E> }
 *
 * @param response The **browser Fetch API** Response object
 * @returns A Promise of `ApiResult<T, E>`
 */
export const handleResponse = async <T, E = undefined>(
  response: globalThis.Response,
): Promise<ApiResult<T, E>> => {
  // If not OK, try to parse the error body
  if (!response.ok) {
    try {
      const body = (await response.json()) as E;
      return {
        ok: false,
        error: new FetchError<E>('Request error', response.status, body),
      };
    } catch {
      // If parsing the body fails, we still throw an error
      return {
        ok: false,
        error: new FetchError('Request error', response.status),
      };
    }
  }

  // 204 No Content => return undefined
  if (response.status === 204) {
    return { ok: true, data: undefined as unknown as T };
  }

  // Otherwise, parse the success payload
  const data = (await response.json()) as T;
  return { ok: true, data };
};

/**
 * Creates the default headers for an authorized request.
 * Includes a bearer token and sets Content-Type to JSON.
 */
const createDefaultAuthorizedHeaders = async () => {
  const token = await getToken();
  const headers = new Headers();
  headers.set('Authorization', `Bearer ${token}`);
  headers.set('Content-Type', 'application/json');
  return headers;
};

const createDefaultHeaders = () => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  return headers;
};

interface HttpClientGetJsonOptions {
  signal?: AbortSignal | null;
}

export const httpClientGetJson = async <T, E = undefined>(
  url: string,
  options?: HttpClientGetJsonOptions,
): Promise<T> => {
  const headers = createDefaultHeaders();
  const response = await fetch(url, { headers, ...options });

  const parsedResponse = await handleResponse<T, E>(response);
  if (!parsedResponse.ok) {
    throw parsedResponse.error;
  }

  return parsedResponse.data;
};

interface HttpClientPostJsonOptions {
  keepalive?: boolean;
}

export const httpClientPostJson = async <T, E = undefined>(
  url: string,
  data?: unknown,
  options?: HttpClientPostJsonOptions,
): Promise<T> => {
  const headers = createDefaultHeaders();
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: data ? JSON.stringify(data) : undefined,
    keepalive: options?.keepalive,
  });

  const parsedResponse = await handleResponse<T, E>(response);
  if (!parsedResponse.ok) {
    throw parsedResponse.error;
  }

  return parsedResponse.data;
};

/**
 * Performs a GET request with default headers, returning the parsed JSON.
 */
export const httpClientGetAuthorizedJson = async <T, E = undefined>(url: string): Promise<T> => {
  const headers = await createDefaultAuthorizedHeaders();
  const response = await fetch(url, { headers });

  const parsedResponse = await handleResponse<T, E>(response);
  if (!parsedResponse.ok) {
    throw parsedResponse.error;
  }

  return parsedResponse.data;
};

/**
 * Performs a POST request (with JSON body) using default headers,
 * then uses handleResponse to parse and validate the result.
 */
export const httpClientPostAuthorizedJson = async <T, E = undefined>(
  url: string,
  data?: unknown,
): Promise<T> => {
  const headers = await createDefaultAuthorizedHeaders();
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  const parsedResponse = await handleResponse<T, E>(response);
  if (!parsedResponse.ok) {
    throw parsedResponse.error;
  }

  return parsedResponse.data;
};

export const httpClientPostFormDataAutorizedJson = async <T, E = undefined>(
  url: string,
  formData: FormData,
): Promise<T> => {
  const headers = await createDefaultAuthorizedHeaders();
  // Remove Content-Type header to allow browser to set it correctly
  headers.delete('Content-Type');
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });
  const parsedResponse = await handleResponse<T, E>(response);
  if (!parsedResponse.ok) {
    throw parsedResponse.error;
  }
  return parsedResponse.data;
};

export const httpClientPutAuthorizedJson = async <T, E = undefined>(
  url: string,
  data?: unknown,
): Promise<T> => {
  const headers = await createDefaultAuthorizedHeaders();
  const response = await fetch(url, {
    method: 'PUT',
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  const parsedResponse = await handleResponse<T, E>(response);
  if (!parsedResponse.ok) {
    throw parsedResponse.error;
  }

  return parsedResponse.data;
};

export const httpClientDeleteAuthorizedJson = async <T, E = undefined>(url: string): Promise<T> => {
  const headers = await createDefaultAuthorizedHeaders();
  const response = await fetch(url, {
    method: 'DELETE',
    headers,
  });

  const parsedResponse = await handleResponse<T, E>(response);
  if (!parsedResponse.ok) {
    throw parsedResponse.error;
  }

  return parsedResponse.data;
};

export const httpClientPostFormEncoded = async <T, E = undefined>(
  url: string,
  data: Record<string, string>,
): Promise<T> => {
  const formData = new URLSearchParams();
  Object.entries(data).forEach(([key, value]) => {
    formData.append(key, value);
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData.toString(),
  });

  const parsedResponse = await handleResponse<T, E>(response);
  if (!parsedResponse.ok) {
    throw parsedResponse.error;
  }

  return parsedResponse.data;
};
